import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  ArrowLeftIcon,
  FilterIcon,
  WaitlistIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { HStack } from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { DiningGroupSizeSelector } from "../../../../screens/AgendaScreen/AgendaScreens/DiningAgendaScreen/components/DiningGroupSizeSelector";
import { SelectDateButton } from "../../../Agenda/components/SelectDateButton";
import { FacilityLogo } from "../../../Session/components/FacilityLogo";
import { TeeSheetAgendaHeaderFragment_club$key } from "./__generated__/TeeSheetAgendaHeaderFragment_club.graphql";

type TeeSheetAgendaHeaderProps = {
  onBackPress?: () => void;
  isScrolled: boolean;
  onFilterPress: () => void;
  onDatePress: () => void;
  onWaitlistPress?: () => void;
  title: string;
  dateString: string;
  showWaitlist?: boolean;
  variant?: "GOLF_COURSE" | "MULTI_SLOT" | "DINING";
  clubRef: TeeSheetAgendaHeaderFragment_club$key;
};

export const TeeSheetAgendaHeader = ({
  onBackPress,
  onFilterPress,
  onDatePress,
  onWaitlistPress,
  isScrolled,
  title,
  dateString,
  showWaitlist,
  variant,
  clubRef,
}: TeeSheetAgendaHeaderProps) => {
  // When using agenda split, I need to specify for all sizes
  const isMobileLayout = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: false,
    lg: false,
    xl: false,
  });

  const showFilters = isMobileLayout;
  const showDateButton = isMobileLayout;

  const club = useFragment(TeeSheetAgendaHeaderFragmentString, clubRef);

  const isRounded = variant !== "MULTI_SLOT";

  const bottomComponent =
    isMobileLayout && variant === "DINING" ? <DiningGroupSizeSelector /> : null;

  return (
    <ScreenHeader
      subHeading={club?.name}
      variant={isMobileLayout ? "CONDENSED" : "REGULAR"}
      isRounded={isRounded}
      scrolled={isScrolled}
      leftItem={
        onBackPress ? (
          <Button
            accessibilityLabel={t`Back button`}
            variant="secondary"
            leftIcon={<ArrowLeftIcon />}
            onPress={onBackPress}
            testID="mobile-header-back-button"
          />
        ) : null
      }
      rightItem={
        <HStack gap={2}>
          {showWaitlist ? (
            <Button
              accessibilityLabel={t`Waitlist button`}
              variant="secondary"
              leftIcon={<WaitlistIcon />}
              onPress={onWaitlistPress}
              testID="mobile-header-waitlist-button"
            />
          ) : null}
          {showDateButton ? (
            <SelectDateButton dateString={dateString} onPress={onDatePress} />
          ) : null}
          {showFilters ? (
            <Button
              variant="secondary"
              accessibilityLabel={t`Filter button`}
              leftIcon={<FilterIcon />}
              onPress={onFilterPress}
              testID="mobile-header-filter-button"
            />
          ) : null}
        </HStack>
      }
      titleRightElement={
        <FacilityLogo
          clubRef={club ?? null}
          imageProps={{
            style: { height: 56, width: 56, borderRadius: 24 },
          }}
        />
      }
      title={title}
      testID="agenda-header-screen-header"
      bottomComponent={bottomComponent}
    />
  );
};

export const TeeSheetAgendaHeaderFragmentString = graphql`
  fragment TeeSheetAgendaHeaderFragment_club on Club {
    name
    ...FacilityLogoFragment_club
  }
`;
